import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, Button, Stack, Chip, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert,
} from "@mui/material";
import CourseApi, { CourseSummary, Course } from "../api/CourseApi";
import SelfTestApi, { SelfTestSummary, SelfTestDetail } from "../api/SelfTestApi";

// Read-only preview of a full course: modules → lessons → sections. Deliberately
// non-interactive (no progress, journal or sliders) — the provider only reads
// the material to decide what to assign.
function CourseContentView({ course }: { course: Course }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={3}>
      {course.modules.map((m, mi) => (
        <Box key={mi}>
          <Typography variant="h3" sx={{ fontSize: "1.1rem", mb: m.summary ? 0.5 : 1 }}>{m.title}</Typography>
          {m.summary && <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mb: 1 }}>{m.summary}</Typography>}
          <Stack spacing={1.5} sx={{ pl: { sm: 1.5 }, borderLeft: { sm: "2px solid var(--border)" } }}>
            {m.lessons.map((l, li) => (
              <Box key={li}>
                <Typography sx={{ fontWeight: 600 }}>
                  {l.title}
                  {typeof l.durationMin === "number" && l.durationMin > 0 && (
                    <Chip size="small" variant="outlined" label={t("catalog.minutes", { n: l.durationMin })} sx={{ ml: 1 }} />
                  )}
                </Typography>
                {(l.sections || []).map((s, si) => (
                  <Box key={si} sx={{ mt: 1 }}>
                    {s.heading && <Typography sx={{ fontWeight: 600, fontSize: ".92rem" }}>{s.heading}</Typography>}
                    <Typography sx={{ fontSize: ".92rem", color: "text.secondary", whiteSpace: "pre-line" }}>{s.body}</Typography>
                    {s.options && s.options.length > 0 && (
                      <Box component="ul" sx={{ m: 0.5, pl: 3 }}>
                        {s.options.map((o, oi) => <Typography component="li" key={oi} sx={{ fontSize: ".88rem", color: "text.secondary" }}>{o}</Typography>)}
                      </Box>
                    )}
                    {(s.kind === "media" || s.kind === "breathing") && (
                      <Chip size="small" variant="outlined" label={t(`catalog.sectionKind.${s.kind}`)} sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                ))}
                {l.journalPrompt && (
                  <Typography sx={{ fontSize: ".88rem", color: "text.secondary", fontStyle: "italic", mt: 1 }}>
                    {t("catalog.journalPrompt")}: {l.journalPrompt}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

// Read-only preview of a questionnaire: intro, scale and the numbered questions.
// No answering — the provider never fills these in, and never sees answers.
function SelfTestContentView({ test }: { test: SelfTestDetail }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <Typography sx={{ color: "text.secondary" }}>{test.intro}</Typography>
      {test.scaleLabels?.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mb: 0.5 }}>{t("catalog.scale")}</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {test.scaleLabels.map((l, i) => <Chip key={i} size="small" variant="outlined" label={l} />)}
          </Stack>
        </Box>
      )}
      <Box component="ol" sx={{ m: 0, pl: 3, display: "grid", gap: 1 }}>
        {test.questions.map((q, i) => <Typography component="li" key={i} sx={{ fontSize: ".95rem" }}>{q.text}</Typography>)}
      </Box>
      {test.source && <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>{t("catalog.source")}: {test.source}</Typography>}
    </Stack>
  );
}

export interface AssignTarget {
  clientName: string;
  // Resolves when the assignment is stored; the browser closes its viewer after.
  onAssign: (sel: { courseSlug?: string; selfTestSlug?: string; moduleIndex?: number; note?: string }) => Promise<void>;
}

// Browsable catalogue of assignable courses + reflection questionnaires. Used
// both as a standalone "Bibliotheek" (assignTarget undefined) and inside the
// per-client assign dialog (assignTarget set → each preview can be assigned).
export default function CatalogBrowser({ assignTarget }: { assignTarget?: AssignTarget }) {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [selftests, setSelftests] = useState<SelfTestSummary[]>([]);
  // Open viewer: {kind, slug} → fetches detail on demand.
  const [open, setOpen] = useState<{ kind: "course" | "selftest"; slug: string; title: string } | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [test, setTest] = useState<SelfTestDetail | null>(null);
  const [moduleIndex, setModuleIndex] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignErr, setAssignErr] = useState("");
  useEffect(() => { setAssignErr(""); }, [open]);

  useEffect(() => {
    CourseApi.list().then(setCourses).catch(() => {});
    SelfTestApi.list().then(setSelftests).catch(() => {});
  }, []);

  const view = (kind: "course" | "selftest", slug: string, title: string) => {
    setOpen({ kind, slug, title });
    setCourse(null); setTest(null); setModuleIndex(""); setNote("");
    if (kind === "course") CourseApi.get(slug).then(setCourse).catch(() => {});
    else SelfTestApi.get(slug).then(setTest).catch(() => {});
  };

  const doAssign = async () => {
    if (!open || !assignTarget) return;
    setBusy(true);
    setAssignErr("");
    try {
      await assignTarget.onAssign(
        open.kind === "course"
          ? { courseSlug: open.slug, moduleIndex: moduleIndex === "" ? undefined : Number(moduleIndex), note: note || undefined }
          : { selfTestSlug: open.slug, note: note || undefined }
      );
      setOpen(null);
    } catch (e: any) {
      // 409 = al toegewezen aan deze cliënt; toon de servermelding.
      setAssignErr(e?.response?.data?.error || t("assign.assignFailed"));
    } finally { setBusy(false); }
  };

  const card = (title: string, body: string, chips: React.ReactNode, onView: () => void) => (
    <Box sx={{ p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mb: 1.5 }}>{body}</Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}>{chips}</Stack>
      <Button size="small" variant="outlined" onClick={onView}>{t("catalog.viewContent")}</Button>
    </Box>
  );

  return (
    <Box>
      {courses.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 1.5 }}>{t("catalog.courses")}</Typography>
          <Stack spacing={1.5}>
            {courses.map((c) => card(
              c.title, c.summary,
              <>
                <Chip size="small" variant="outlined" label={t("catalog.moduleCount", { n: c.moduleCount })} />
                <Chip size="small" variant="outlined" label={t("catalog.lessonCount", { n: c.lessonCount })} />
                {c.visibility === "assignable" && <Chip size="small" color="secondary" label={t("assign.deepening")} />}
              </>,
              () => view("course", c.slug, c.title),
            ))}
          </Stack>
        </Box>
      )}
      {selftests.length > 0 && (
        <Box>
          <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 1.5 }}>{t("catalog.selftests")}</Typography>
          <Stack spacing={1.5}>
            {selftests.map((s) => card(
              s.title, s.intro,
              <>
                <Chip size="small" variant="outlined" label={t("catalog.questionCount", { n: s.questionCount })} />
                {s.visibility === "assignable" && <Chip size="small" color="info" label={t("assign.reflection")} />}
              </>,
              () => view("selftest", s.slug, s.title),
            ))}
          </Stack>
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{open?.title}</DialogTitle>
        <DialogContent dividers>
          {open?.kind === "course"
            ? (course ? <CourseContentView course={course} /> : <Box sx={{ py: 4, display: "grid", placeItems: "center" }}><CircularProgress size={28} sx={{ color: "primary.main" }} /></Box>)
            : (test ? <SelfTestContentView test={test} /> : <Box sx={{ py: 4, display: "grid", placeItems: "center" }}><CircularProgress size={28} sx={{ color: "primary.main" }} /></Box>)}
          {assignTarget && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2 }}>{t("assign.privacyBanner")}</Alert>
              {open?.kind === "course" && course && course.modules.length > 0 && (
                <TextField select fullWidth label={t("assign.module")} value={moduleIndex}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setModuleIndex(e.target.value)} sx={{ mb: 2 }}>
                  <MenuItem value="">{t("assign.wholeCourse")}</MenuItem>
                  {course.modules.map((m, i) => <MenuItem key={i} value={String(i)}>{m.title}</MenuItem>)}
                </TextField>
              )}
              <TextField fullWidth multiline minRows={2} label={t("assign.note")} value={note}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)} />
            </Box>
          )}
        </DialogContent>
        {assignErr && <Alert severity="warning" sx={{ mx: 3, mb: 1 }} onClose={() => setAssignErr("")}>{assignErr}</Alert>}
        <DialogActions>
          <Button onClick={() => setOpen(null)}>{t("catalog.close")}</Button>
          {assignTarget && (
            <Button variant="contained" onClick={doAssign} disabled={busy || (open?.kind === "course" ? !course : !test)}>
              {t("assign.assignTo", { name: assignTarget.clientName })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
